import React from "react";
import { RotateCcw } from "lucide-react";
import { Branch, Chips, TreeHeader } from "./FaultTreePrimitives";

/**
 * Intermittent fault tree — DEEP, 6 branching levels.
 *
 *   1. What's intermittent? (warning light / noise / behaviour / smell / electrical)
 *   2. Frequency (daily / weekly / monthly / unpredictable)
 *   3. Last occurrence (today / this week / longer)
 *   4. Reproducibility (always with X / sometimes / never)
 *   5. Conditions when present (cold / hot / wet / hills / motorway)
 *   6. Notes — customer's own words
 */
export default function IntermittentFaultTree({
  data = {},
  onChange = () => {},
  onReset,
}) {
  const showFreq = !!data.fault_type;
  const showLast = !!data.frequency;
  const showRepro = !!data.last_occurrence;
  const showCond = !!data.reproducibility;
  const showNotes = !!data.fault_type;

  return (
    <div
      className="rounded-lg border border-slate-200 bg-white overflow-hidden"
      data-testid="intermittent-fault-tree"
    >
      <TreeHeader
        eyebrow="Diagnostic · Fault tree"
        title="Intermittent fault triage"
        tone="amber"
        right={
          onReset && (
            <button
              type="button"
              onClick={onReset}
              className="inline-flex items-center gap-1 text-[12.5px] text-slate-500 hover:text-slate-800"
              data-testid="intermittent-reset"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
          )
        }
      />

      <div className="p-4 space-y-3.5">
        <Branch
          step={1}
          label="What kind of intermittent fault?"
          subtitle="Intermittents are the hardest to catch — frame the right capture pattern from the start."
          testId="branch-int-type"
        >
          <Chips
            tone="amber"
            value={data.fault_type}
            options={[
              { v: "warning_light", label: "Warning light" },
              { v: "noise", label: "Noise / vibration" },
              { v: "behaviour", label: "Drive behaviour" },
              { v: "no_start", label: "Won’t start" },
              { v: "electrical", label: "Electrical" },
              { v: "smell", label: "Smell" },
              { v: "other", label: "Other / unsure" },
            ]}
            onChange={(v) =>
              onChange({
                fault_type: v,
                frequency: null,
                last_occurrence: null,
                reproducibility: null,
                conditions: [],
              })
            }
            testId="int-type"
          />
        </Branch>

        {showFreq && (
          <Branch step={2} label="How often does it happen?" testId="branch-int-frequency">
            <Chips
              tone="amber"
              value={data.frequency}
              options={[
                { v: "daily", label: "Daily" },
                { v: "weekly", label: "A few times a week" },
                { v: "monthly", label: "A few times a month" },
                { v: "rare", label: "Only happened once or twice" },
                { v: "unpredictable", label: "Unpredictable" },
              ]}
              onChange={(v) => onChange({ frequency: v })}
              testId="int-frequency"
            />
          </Branch>
        )}

        {showLast && (
          <Branch step={3} label="When did it last happen?" testId="branch-int-last">
            <Chips
              tone="amber"
              value={data.last_occurrence}
              options={[
                { v: "today", label: "Today" },
                { v: "yesterday", label: "Yesterday" },
                { v: "this_week", label: "This week" },
                { v: "older", label: "Longer ago" },
                { v: "in_for_repair_only", label: "Only when in for repair" },
              ]}
              onChange={(v) => onChange({ last_occurrence: v })}
              testId="int-last"
            />
          </Branch>
        )}

        {showRepro && (
          <Branch
            step={4}
            label="Can you reproduce it?"
            subtitle="Reproducible faults are far cheaper to diagnose — set expectations here."
            testId="branch-int-repro"
          >
            <Chips
              tone="amber"
              value={data.reproducibility}
              options={[
                { v: "always_with_x", label: "Always with a specific trigger" },
                { v: "sometimes", label: "Sometimes" },
                { v: "never", label: "No — fully random" },
                { v: "stops_for_us", label: "Stops the moment we look at it" },
              ]}
              onChange={(v) => onChange({ reproducibility: v })}
              testId="int-repro"
            />
          </Branch>
        )}

        {showCond && (
          <Branch
            step={5}
            label="Conditions when it appears"
            subtitle="Tick any that apply — environment + use pattern matter."
            testId="branch-int-conditions"
          >
            <Chips
              tone="amber"
              multi
              value={data.conditions || []}
              options={[
                { v: "cold_start", label: "Cold start" },
                { v: "hot_engine", label: "Hot engine / long drive" },
                { v: "wet_weather", label: "Wet weather" },
                { v: "hills", label: "Going uphill" },
                { v: "motorway", label: "On motorway" },
                { v: "stop_start", label: "Stop-start traffic" },
                { v: "after_fuel", label: "After fuel fill" },
                { v: "after_wash", label: "After car wash" },
                { v: "no_pattern", label: "No pattern" },
              ]}
              onChange={(arr) => onChange({ conditions: arr })}
              testId="int-conditions"
            />
          </Branch>
        )}

        {showNotes && (
          <Branch
            step={6}
            label="Customer’s own description"
            testId="branch-int-notes"
          >
            <textarea
              rows={2}
              value={data.notes || ""}
              onChange={(e) => onChange({ notes: e.target.value })}
              placeholder="e.g. ‘only ever happens when it’s been parked overnight in the rain’"
              className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-[15px] text-slate-900 resize-none focus:outline-none focus:border-amber-500"
              data-testid="int-notes"
            />
          </Branch>
        )}
      </div>
    </div>
  );
}
